// Confederaciones — niveles superiores de la estructura (Tercer y Cuarto piso).
// DATOS REALES: confederaciones/federaciones de la base de datos y conteos
// de GET organizacion/resumen (cooperativas activas, afiliados activos).
//
// La Confederación Internacional (cuarto piso) es una entidad persistida
// (tabla confederaciones_internacionales, migración 010): sus datos salen de
// la base de datos. Lo que no esté registrado no se muestra ni se inventa.

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  Business as BusinessIcon,
  Gavel as GavelIcon,
  Groups as GroupsIcon,
  Public as PublicIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useRedReal from '../hooks/useRedReal';
import { fecha } from '../utils/formato';

const fmt = (n) => Number(n || 0).toLocaleString('es-CO');
const plural = (n, uno, varios) => `${fmt(n)} ${n === 1 ? uno : varios}`;

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });

const Confederaciones = () => {
  const { internacionales, confederaciones, federaciones, totales, conteos, cargando, error, recargar, bandera, nombrePais } = useRedReal();
  const [tab, setTab] = useState(0);
  const [detalle, setDetalle] = useState(null); // confederación seleccionada

  const ordenadas = useMemo(() => [...confederaciones].sort((a, b) => a.nombre.localeCompare(b.nombre)), [confederaciones]);

  // Países distintos de la red (una entrada por país).
  const paises = useMemo(() => {
    const mapa = new Map();
    confederaciones.forEach((c) => {
      const clave = (c.pais || '').toLowerCase();
      if (!clave) return;
      if (!mapa.has(clave)) mapa.set(clave, { nombre: c.pais, paisId: c.paisId, confederaciones: 0 });
      mapa.get(clave).confederaciones += 1;
    });
    return [...mapa.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [confederaciones]);

  const federacionesDe = (confederacionId) =>
    federaciones.filter((f) => f.confederacionId === confederacionId).sort((a, b) => a.nombre.localeCompare(b.nombre));

  const stats = [
    { title: 'Confederación Internacional', value: totales.confederacionesInternacionales, icon: PublicIcon, color: '#1B5E20', subtitle: 'Cuarto piso' },
    {
      title: 'Confederaciones Nacionales',
      value: totales.confederaciones,
      icon: GavelIcon,
      color: '#0D47A1',
      subtitle: `Tercer piso • ${plural(totales.paises, 'país', 'países')}`,
    },
    { title: 'Federaciones', value: totales.federaciones, icon: ApartmentIcon, color: '#E65100', subtitle: 'Segundo piso' },
    { title: 'Cooperativas', value: totales.cooperativas, icon: BusinessIcon, color: '#6A1B9A', subtitle: 'Primer piso' },
  ];

  const piramide = [
    {
      ancho: '40%',
      fondo: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
      icono: PublicIcon,
      titulo: 'Confederación Internacional',
      detalle: `Cuarto Piso • ${plural(totales.confederacionesInternacionales, 'organización', 'organizaciones')}`,
    },
    {
      ancho: '55%',
      fondo: 'linear-gradient(135deg, #0D47A1, #1565C0)',
      icono: GavelIcon,
      titulo: 'Confederaciones Nacionales',
      detalle: `Tercer Piso • ${plural(totales.confederaciones, 'confederación', 'confederaciones')} • ${plural(totales.paises, 'país', 'países')}`,
    },
    {
      ancho: '70%',
      fondo: 'linear-gradient(135deg, #E65100, #F57C00)',
      icono: ApartmentIcon,
      titulo: 'Federaciones',
      detalle: `Segundo Piso • ${plural(totales.federaciones, 'federación', 'federaciones')}`,
    },
    {
      ancho: '85%',
      fondo: 'linear-gradient(135deg, #6A1B9A, #8E24AA)',
      icono: BusinessIcon,
      titulo: 'Cooperativas',
      detalle: `Primer Piso • ${plural(totales.cooperativas, 'cooperativa activa', 'cooperativas activas')}`,
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Confederaciones
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Niveles superiores de la estructura cooperativa (Tercer y Cuarto piso)
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={recargar}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Totales */}
          <Box sx={{ ...grid(220), mb: 3 }}>
            {stats.map((s) => (
              <Card key={s.title}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}1A`, color: s.color, display: 'flex' }}>
                    <s.icon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: s.color }}>
                      {fmt(s.value)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {s.title}
                    </Typography>
                    <Typography variant="caption">{s.subtitle}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab icon={<PublicIcon />} iconPosition="start" label="Confederación Internacional" />
              <Tab icon={<GavelIcon />} iconPosition="start" label="Confederaciones Nacionales" />
              <Tab icon={<ApartmentIcon />} iconPosition="start" label="Visión General" />
            </Tabs>

            <CardContent>
              {/* ── Confederación internacional (cuarto piso) ── */}
              <TabPanel value={tab} index={0}>
                {internacionales.length === 0 ? (
                  <Typography color="text.secondary">
                    Aún no hay una confederación internacional registrada. Créala o impórtala desde «Administración de la Red».
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {internacionales.map((i) => {
                      const n = conteos.internacionales.get(i.id) || {};
                      const datos = [
                        ['Representante legal', i.representanteLegal],
                        ['NIT', i.nit],
                        ['Correo', i.email],
                        ['Teléfono', i.telefono],
                        ['Sitio web', i.sitioWeb],
                        ['País', nombrePais(i.paisId)],
                        ['Dirección', i.direccion],
                        ['Constituida', i.fechaConstitucion && fecha(i.fechaConstitucion)],
                      ].filter(([, v]) => v);
                      return (
                        <Card key={i.id} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                              <Typography variant="h5" fontWeight="bold">
                                {i.nombre}
                              </Typography>
                              {i.siglas && <Chip size="small" label={i.siglas} />}
                              <Chip size="small" color={i.activa ? 'success' : 'default'} label={i.activa ? 'Activa' : 'Inactiva'} />
                            </Box>
                            {datos.length > 0 && (
                              <Box sx={{ ...grid(200), mb: 2 }}>
                                {datos.map(([titulo, valor]) => (
                                  <Box key={titulo}>
                                    <Typography variant="caption" color="text.secondary">
                                      {titulo}
                                    </Typography>
                                    <Typography variant="body2">{valor}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {[
                              ['Descripción', i.descripcion],
                              ['Misión', i.mision],
                              ['Visión', i.vision],
                            ]
                              .filter(([, v]) => v)
                              .map(([titulo, valor]) => (
                                <Box key={titulo} sx={{ mb: 1.5 }}>
                                  <Typography variant="subtitle2">{titulo}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {valor}
                                  </Typography>
                                </Box>
                              ))}
                            <Divider sx={{ my: 2 }} />
                            <Box sx={grid(160)}>
                              {[
                                ['Confederaciones', n.confederaciones],
                                ['Federaciones', n.federaciones],
                                ['Cooperativas activas', n.cooperativas],
                                ['Afiliados activos', n.afiliados],
                              ].map(([titulo, valor]) => (
                                <Paper key={titulo} variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography variant="h5" fontWeight="bold" color="primary">
                                    {fmt(valor)}
                                  </Typography>
                                  <Typography variant="body2">{titulo}</Typography>
                                </Paper>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                    {paises.length > 0 && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Países de la red
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {paises.map((p) => (
                            <Chip key={p.nombre} label={`${bandera(p.paisId, p.nombre)} ${p.nombre}`} variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </TabPanel>

              {/* ── Confederaciones nacionales ── */}
              <TabPanel value={tab} index={1}>
                {ordenadas.length === 0 ? (
                  <Typography color="text.secondary">
                    Aún no hay confederaciones. Créalas o impórtalas desde «Administración de la Red».
                  </Typography>
                ) : (
                  <Box sx={grid(320)}>
                    {ordenadas.map((c) => {
                      const n = conteos.confederaciones.get(c.id) || {};
                      return (
                        <Card key={c.id} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                              <Typography variant="h6">{c.nombre}</Typography>
                              <Chip size="small" label={`${bandera(c.paisId, c.pais)} ${c.pais}`} />
                            </Box>
                            {c.fechaConstitucion && (
                              <Typography variant="caption" color="text.secondary">
                                Constituida el {fecha(c.fechaConstitucion)}
                              </Typography>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                              {[
                                ['Federaciones', n.federaciones, 'primary'],
                                ['Cooperativas', n.cooperativas, 'secondary'],
                                ['Afiliados', n.afiliados, 'success.main'],
                              ].map(([titulo, valor, color]) => (
                                <Box key={titulo}>
                                  <Typography variant="h5" fontWeight="bold" color={color}>
                                    {fmt(valor)}
                                  </Typography>
                                  <Typography variant="caption">{titulo}</Typography>
                                </Box>
                              ))}
                            </Box>
                            <Box sx={{ mt: 2, textAlign: 'right' }}>
                              <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setDetalle(c)}>
                                Ver federaciones
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </TabPanel>

              {/* ── Visión general (pirámide con conteos reales) ── */}
              <TabPanel value={tab} index={2}>
                <Typography variant="h6" gutterBottom>
                  Estructura Jerárquica del Sistema Cooperativo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Cada nivel delega funciones y recursos, manteniendo autonomía pero operando bajo una red colaborativa unificada.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  {piramide.map((n) => (
                    <Paper
                      key={n.titulo}
                      sx={{ width: n.ancho, p: 3, background: n.fondo, color: 'white', textAlign: 'center', borderRadius: 2 }}
                    >
                      <n.icono sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="inherit">
                        {n.titulo}
                      </Typography>
                      <Typography variant="caption" color="inherit">
                        {n.detalle}
                      </Typography>
                    </Paper>
                  ))}
                  <Paper sx={{ width: '100%', p: 3, bgcolor: 'grey.100', textAlign: 'center', borderRadius: 2 }}>
                    <GroupsIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Afiliados
                    </Typography>
                    <Typography variant="caption">Base • {plural(totales.afiliados, 'afiliado activo', 'afiliados activos')}</Typography>
                  </Paper>
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </>
      )}

      {/* Detalle de una confederación: sus federaciones */}
      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        {detalle && (
          <>
            <DialogTitle>
              {detalle.nombre}
              <Typography variant="body2" color="text.secondary">
                {bandera(detalle.paisId, detalle.pais)} {detalle.pais}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {federacionesDe(detalle.id).length === 0 ? (
                <Typography color="text.secondary">Esta confederación aún no tiene federaciones.</Typography>
              ) : (
                <List disablePadding>
                  {federacionesDe(detalle.id).map((f) => {
                    const n = conteos.federaciones.get(f.id) || {};
                    return (
                      <ListItem key={f.id} divider>
                        <ListItemText
                          primary={f.nombre}
                          secondary={`${plural(n.cooperativas, 'cooperativa', 'cooperativas')} • ${plural(n.afiliados, 'afiliado', 'afiliados')}`}
                        />
                        {f.especializacion && <Chip size="small" label={f.especializacion} />}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalle(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Confederaciones;
